import React, { useEffect, useRef } from "react";
import ReactDOMServer from "react-dom/server"; // Import ReactDOMServer
import {
  DEFAULT_LOYALTY_TINT,
  useLoyaltyAccent,
} from "../../../services/hooks/loyalty/useLoyaltyTierTheme";
import { TwistIconWithBackground } from "../Icons/Icons";
import styles from "./AnimatedConfettiComponent.module.scss";
import {
  Icon_1,
  Icon_2,
  Icon_3,
  Icon_4,
  Icon_5,
  Icon_6,
  Icon_7,
  Icon_8,
  Icon_9,
} from "./icons/icons";

interface ConfettiComponentProps {
  twistIcon?: boolean;
  darkColor?: boolean;
  /** Explicit piece colour, e.g. the active loyalty tier colour. */
  color?: string;
  /**
   * Opt in to the active loyalty tier's tint. Off by default so unrelated
   * celebrations keep the fixed cream pieces on a tiered restaurant.
   */
  tierTint?: boolean;
  /**
   * Keeps the pieces inside the host element instead of falling the full
   * viewport height. Used by the tier cards, which are only 123px tall.
   */
  contained?: boolean;
}

const withConfettiFade = (tint: string) =>
  tint.startsWith("#") ? `${tint}1A` : tint.replace(")", " / 10%)");

const SPEEDS = ["slow", "medium", "fast"] as const;

// Figma scatters ~14 pieces across a tier card. With 5-15s falls, a piece every
// 700ms keeps roughly that many alive; 30 is the mobile widget's hard ceiling.
const CONTAINED_SPAWN_MS = 700;
const CONTAINED_MAX_PIECES = 30;
const CONTAINED_LIFETIME_MS = 15000;

const ConfettiComponent: React.FC<ConfettiComponentProps> = ({
  twistIcon,
  darkColor,
  color,
  tierTint,
  contained,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { tint: accentTint } = useLoyaltyAccent();
  const tint = (tierTint ? accentTint : undefined) ?? DEFAULT_LOYALTY_TINT;
  const pieceColor = color ?? (darkColor ? withConfettiFade(tint) : tint);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const markup = (
      twistIcon
        ? [<TwistIconWithBackground key="twist" />]
        : [
            <Icon_1 key="1" fill={pieceColor} />,
            <Icon_2 key="2" fill={pieceColor} />,
            <Icon_3 key="3" fill={pieceColor} />,
            <Icon_4 key="4" fill={pieceColor} />,
            <Icon_5 key="5" fill={pieceColor} />,
            <Icon_6 key="6" fill={pieceColor} />,
            <Icon_7 key="7" fill={pieceColor} />,
            <Icon_8 key="8" fill={pieceColor} />,
            <Icon_9 key="9" fill={pieceColor} />,
          ]
    ).map((icon) => ReactDOMServer.renderToString(icon));

    const timeouts = new Set<ReturnType<typeof setTimeout>>();
    // Contained pieces mirror the mobile ConfettiAnimation's sizing and speeds,
    // at the Figma card density and opacity.
    const spawnInterval = contained
      ? CONTAINED_SPAWN_MS
      : twistIcon
      ? 1000
      : 150;
    const lifetime = contained ? CONTAINED_LIFETIME_MS : 8000;

    const confettiInterval = setInterval(() => {
      if (contained && container.childElementCount >= CONTAINED_MAX_PIECES) {
        return;
      }

      const confettiEl = document.createElement("div");
      const speed = twistIcon
        ? "slow"
        : SPEEDS[Math.floor(Math.random() * SPEEDS.length)];

      confettiEl.className = contained
        ? `${styles.confetti} ${styles.confetti_contained} ${
            styles["confetti_contained_" + speed]
          }`
        : `${styles.confetti} ${styles["confetti__animation_" + speed]}`;
      confettiEl.style.left = `${Math.floor(
        Math.random() * (container.clientWidth || window.innerWidth)
      )}px`;

      if (contained) {
        // Pieces drop exactly the host's height, as the mobile widget does.
        confettiEl.style.setProperty(
          "--confetti-fall",
          `${container.clientHeight}px`
        );
      }

      const iconSpan = document.createElement("span");
      iconSpan.innerHTML = markup[Math.floor(Math.random() * markup.length)];
      confettiEl.appendChild(iconSpan);

      const removeTimeout = setTimeout(() => {
        confettiEl.remove();
        timeouts.delete(removeTimeout);
      }, lifetime);

      timeouts.add(removeTimeout);
      container.appendChild(confettiEl);
    }, spawnInterval);

    return () => {
      clearInterval(confettiInterval);
      timeouts.forEach(clearTimeout);
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
    };
  }, [twistIcon, pieceColor, contained]);

  return (
    <div className={styles.container}>
      <div ref={containerRef} className={styles.confetti_container} />
    </div>
  );
};

export default ConfettiComponent;
